import Hero from "../components/Hero";
import CTA from "../components/CTA";
import StyleQuiz from "../components/StyleQuiz";
import StudioNote from "../components/StudioNote";
import { Sparkles } from "lucide-react";
import {
  founderImage,
  teamImage,
  philosophyImage,
  approachImage,
  servicesImage,
  featureImage,
  testimonials,
  brand,
} from "../data/mock";
import useReveal from "../hooks/useReveal";
import { Link } from "react-router-dom";

export default function Home() {
  useReveal();
  return (
    <>
      <Hero />

      {/* Style Quiz — first interactive lead magnet, immediately below hero */}
      <StyleQuiz />

      {/* Intro / About block */}
      <section className="py-24 md:py-36">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-5 reveal">
            <img src={founderImage} alt="Founder Shubhra" className="w-full h-auto" />
          </div>
          <div className="md:col-span-7 reveal">
            <div className="kicker">Hi, I&rsquo;m Shubhra.</div>
            <h2 className="font-display mt-6 text-3xl md:text-5xl leading-[1.1]">
              I don&rsquo;t just plan <Link to="/weddings" className="italic link-underline">luxury weddings</Link> and{" "}
              <Link to="/events" className="italic link-underline">bespoke events</Link>. I craft meaningful
              experiences that come straight from the heart.
            </h2>
            <p className="mt-8 max-w-xl text-[hsl(var(--ink-soft))] font-body leading-relaxed">
              With six years of experience across intimate ceremonies and grand celebrations,
              Shobha with Shubhra brings together heritage, design and precision to create weddings
              and events that feel deeply personal — and unmistakably yours.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <CTA to="/about" label="About" />
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event("sws:open-mood-board"))}
                data-testid="shubhra-mood-board-cta"
                className="inline-flex items-center gap-2 border border-[hsl(var(--ink))] text-[hsl(var(--ink))] px-6 py-3 text-[11px] tracking-[0.3em] uppercase hover:bg-[hsl(var(--accent))] hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--bg))] transition-colors duration-500"
              >
                <Sparkles size={14} /> Try Vision Studio
              </button>
            </div>
            <p className="mt-4 text-xs md:text-sm text-[hsl(var(--ink-soft))] italic max-w-lg">
              Curious what your day could look like? Sketch a personal concept board in seconds —
              reviewed by Shubhra, always.
            </p>

            <div className="mt-16 img-zoom">
              <img src={teamImage} alt="The Shobha with Shubhra team" className="w-full h-[360px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="bg-[hsl(var(--bg-alt))] py-24 md:py-36">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-7 reveal img-zoom">
            <img src={philosophyImage} alt="Philosophy" className="w-full h-[520px] object-cover" />
          </div>
          <div className="md:col-span-5 reveal">
            <div className="kicker">Philosophy</div>
            <h3 className="font-display mt-6 text-3xl md:text-5xl leading-[1.1]">
              I <span className="italic">believe</span> in pushing boundaries to create an event that is truly awe-inspiring.
            </h3>
            <div className="mt-10"><CTA to="/weddings" label="Weddings" /></div>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-24 md:py-36">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-5 md:order-1 order-2 reveal">
            <div className="kicker">Approach</div>
            <h3 className="font-display mt-6 text-3xl md:text-5xl leading-[1.1]">
              I <span className="italic">specialise</span> in curating unique and immersive luxury events for life&rsquo;s special moments.
            </h3>
            <div className="mt-10"><CTA to="/events" label="Events" /></div>
          </div>
          <div className="md:col-span-7 md:order-2 order-1 reveal img-zoom">
            <img src={approachImage} alt="Approach" className="w-full h-[520px] object-cover" />
          </div>
        </div>
      </section>

      {/* Feature CTA */}
      <section className="py-24 md:py-32 bg-[hsl(var(--bg))]">
        <div className="mx-auto max-w-[1200px] px-6 text-center">
          <p className="font-display text-2xl md:text-3xl leading-relaxed max-w-3xl mx-auto reveal">
            Get in touch today to discover why {brand.name} is trusted to deliver exceptional celebrations
            and events in some of the Midwest&rsquo;s most prestigious hotels and locations.
          </p>
          <div className="mt-10"><CTA to="/contact" label="Let’s chat" /></div>
        </div>
      </section>

      {/* Full-bleed feature image */}
      <section className="reveal">
        <Link to="/portfolio" className="block img-zoom">
          <img src={featureImage} alt="Portfolio" className="w-full h-[70vh] object-cover" />
        </Link>
      </section>

      {/* Services */}
      <section className="py-24 md:py-36">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-6 reveal img-zoom">
            <img src={servicesImage} alt="Services" className="w-full h-[560px] object-cover" />
          </div>
          <div className="md:col-span-6 reveal">
            <div className="kicker">Our services</div>
            <h3 className="font-display mt-6 text-3xl md:text-5xl leading-[1.1]">
              Experience <span className="italic">weddings</span>, <span className="italic">events</span> and{" "}
              <span className="italic">celebrations</span> like never before — where elegance meets creativity.
            </h3>
            <p className="mt-6 text-[hsl(var(--ink-soft))] leading-relaxed max-w-xl">
              Every detail is curated to reflect the essence of your celebration. Seamlessly executed and
              beautifully styled, each event is truly unforgettable. Allow us to bring your vision to life.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <CTA to="/events" label="Events" />
              <CTA to="/weddings" label="Weddings" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-36">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="kicker text-center">What our clients say...</div>
          <div className="mt-16 grid md:grid-cols-3 gap-10">
            {testimonials.map((t) => (
              <div key={t.id} className="reveal">
                <div className="img-zoom">
                  <img src={t.image} alt={t.author} className="w-full h-80 object-cover" />
                </div>
                <p className="mt-8 font-display text-xl md:text-2xl leading-snug">
                  &ldquo;{t.quote.split(" ").slice(0, 3).join(" ")}{" "}
                  <span className="italic">{t.quote.split(" ").slice(3, 8).join(" ")}</span>{" "}
                  {t.quote.split(" ").slice(8).join(" ")}&rdquo;
                </p>
                <div className="mt-6 kicker text-[hsl(var(--accent))]">{t.author}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Studio Note — 10-15% early-booking narrative */}
      <StudioNote />

      {/* Final CTA */}
      <section className="relative py-32 md:py-44 bg-[hsl(var(--ink))] text-[hsl(var(--bg))]">
        <div className="mx-auto max-w-[1000px] px-6 text-center">
          <img
            src="https://customer-assets-39nsmqrw.emergentagent.net/job_elegant-event-design-1/artifacts/v1a0y2ji_Shobhawithshubhra-logo-final.png"
            alt="Shobha with Shubhra"
            className="mx-auto h-24 md:h-32 w-auto object-contain mb-8"
          />
          <div className="kicker text-white/60">Let us bring your vision to life</div>
          <h3 className="font-display mt-6 text-3xl md:text-5xl leading-tight">
            The <span className="italic">journey</span> to an amazing celebration begins here.
          </h3>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4 md:gap-6">
            <CTA to="/contact" label="Get in touch" tone="light" />
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("sws:open-mood-board"))}
              data-testid="home-mood-board-cta"
              className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 text-[11px] tracking-[0.3em] uppercase hover:bg-[hsl(var(--accent))] hover:border-[hsl(var(--accent))] transition-colors duration-500"
            >
              <Sparkles size={14} /> Try Vision Studio
            </button>
          </div>

          <p className="mt-6 text-sm text-white/60 italic max-w-xl mx-auto">
            Not sure where to start? Describe your vibe — palette, mood, style — and Ayaan will
            sketch a personal concept board in seconds. Reviewed by Shubhra, always.
          </p>
        </div>
      </section>
    </>
  );
}
